import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Filter, SessionCookieService, TableService } from '@docpath/linces-angular-components/common';
import { CardInfo, DpHeaderOption, Table } from '@docpath/webcomponents';
import { faSignal } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { Constants } from 'src/app/constants/constants';
import { RoutingPath } from 'src/app/constants/routing-path';
import { DgeState } from 'src/app/pages/configuration/engines/models/dge';
import { SinclairCard, SinclairCardFilterOption, SinclairCardFilterOptionType } from 'src/app/shared/components/sinclair-cards/model/sinclair-card-type';
import { CloudService } from 'src/app/shared/services/cloud.service';

import { SinclairCardsComponent } from '../../shared/components/sinclair-cards/sinclair-cards.component';
import { AppSession } from '../../shared/models/app-session.model';
import { StatisticsChartComponent } from './components/chart/statistics-chart.component';
import { StatisticsHeaderComponent } from './components/header/statistics-header.component';
import { StatisticsTableComponent } from './components/table/statistics-table.component';
import { StatisticsTableService } from './components/table/statistics-table.service';
import { Job } from './models/job';
import { StatisticsCardResult, StatisticsCardSearch } from './models/statistics';
import { StatisticsService } from './services/statistics.service';
import { StatisticsHttpService } from './services/statistics-http.service';
@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  standalone: true,
  imports: [StatisticsChartComponent, SinclairCardsComponent, StatisticsHeaderComponent, StatisticsTableComponent, TranslateModule]
})
export class StatisticsComponent implements OnInit, OnDestroy {

  private readonly i18nPrefix = 'statistics';

  faSignal = faSignal;
  title = 'statistics.title';

  public CARD_FILTERS_DGES = 'dges';
  public CARD_FILTERS_NUMBER_OF_HOURS = 'numberHours';

  sinclairCards: SinclairCard[] = [];
  cardsInfo: CardInfo[] = [];

  dgesFilter: SinclairCardFilterOption[] = [];
  numberOfHoursFilter: SinclairCardFilterOption[] = [];

  private table?: Table;
  private tableChangedSubscription?: Subscription;
  selectedRow: Job[] = [];
  filters: Filter[] = [];
  private readonly tableName = 'statistics';

  dgeId: number = 0;
  numberHours: number = Constants.NUMBER_OF_HOURS_24;
  customNumberHoursFrom: string = '';
  customNumberHoursTo: string = '';
  lastJobId: number = 0;

  idRefreshProcess = 0;


  private readonly statisticsService = inject(StatisticsService);
  private readonly statisticsHttpService = inject(StatisticsHttpService);
  private readonly statisticsTableService = inject(StatisticsTableService);
  private readonly tableService = inject(TableService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly sessionCookieService = inject(SessionCookieService);
  private readonly cloudService = inject(CloudService);

  constructor() {
    const columns = this.tableService.getColumns(this.tableName),
      defaultColumns = this.statisticsTableService.getDefaultColumns();

    defaultColumns.forEach(defaultColumn => {
      const column = columns.find(column => column.name === defaultColumn.name);
      if (column) {
        defaultColumn.visible = column.visible;
      }
    });

    this.tableService.updateColumns(this.tableName, defaultColumns);

  }

  onTableLoaded(table: Table): void {
    this.table = table;
  }

  ngOnInit(): void {

    this.dgeId = Number(this.activatedRoute.snapshot.paramMap.get('id'));
    this.dgeId = Number.isNaN(this.dgeId) ? 0 : this.dgeId;

    this.loadNumberOfHoursFiltersOptions();
    this.loadDgesFiltersOptions().subscribe(() => {
      this.loadCards();

      this.clearUrl();
      if (this.dgeId > 0) {
        this.updateDgeFilterActiveByDgeIdUrl();
      }
    });

    this.loadCardsInfo();

    this.statisticsService.statisticsSelectsChanged.subscribe(statisticsFilter => {
      this.dgeId = statisticsFilter.dgeId;
      this.numberHours = statisticsFilter.numberHours;
      this.customNumberHoursFrom = statisticsFilter.customNumberHoursFrom;
      this.customNumberHoursTo = statisticsFilter.customNumberHoursTo;
      this.lastJobId = statisticsFilter.lastJobId;
    });

    if (!this.tableChangedSubscription) {
      this.tableChangedSubscription = this.statisticsService.tableChanged.subscribe(() => {
        this.statisticsTableService.loadJobs(true, this.dgeId ?? Constants.DGE_ALL_VALUES, this.numberHours, this.customNumberHoursFrom, this.customNumberHoursTo, 0, this.filters, 1);
      });
    }

    const appSession = this.sessionCookieService.getSessionCookie()?.app as AppSession;
    this.initRefreshProcess(appSession.refreshLastHour * 1000);

  }

  private initRefreshProcess(refreshLastHour: number) {

    this.idRefreshProcess = setInterval(() => {
      // Refresh process only if the number of hours is different to 0 (Custom search)
      // and if all home elements to refresh are ready
      if (this.numberHours !== Constants.NUMBER_OF_HOURS_0
        && this.statisticsService.isStatisticsElementsReadyToRefresh()) {
        // reset statistics elements ready to refresh
        this.statisticsService.resetStatisticsElementsReadyToRefresh();
        // Refresh cards info
        this.loadCardsInfoValue();
        // Refresh Charts
        this.statisticsService.notifyStatisticsRefreshChange(this.dgeId, this.numberHours);
      }
    }, refreshLastHour) as unknown as number;

  }

  ngOnDestroy() {

    if (this.idRefreshProcess) {
      clearInterval(this.idRefreshProcess);
    }

    this.tableChangedSubscription?.unsubscribe();

  }
  loadCards(): void {

    this.sinclairCards = [
      new SinclairCard({
        name: this.CARD_FILTERS_DGES, rows: 8, title: this.dgesFilter.find(f => f.active)?.title as string,
        subTitle: this.dgesFilter.find(f => f.active)?.subTitle as string,
        tooltip: this.dgesFilter.find(f => f.active)?.tooltip as string,
        filters: this.dgesFilter, filtersHeader: `${this.i18nPrefix}.cards.filters.dges.header`,
        filtersOptionTooltip: `${this.i18nPrefix}.cards.filters.dges.optionsTooltip`
      }),
      new SinclairCard({
        name: this.CARD_FILTERS_NUMBER_OF_HOURS, rows: 4, title: this.numberOfHoursFilter.find(f => f.active)?.title as string,
        tooltip: this.numberOfHoursFilter.find(f => f.active)?.tooltip as string, filters: this.numberOfHoursFilter,
        filtersOptionTooltip: `${this.i18nPrefix}.cards.filters.number-hours.optionsTooltip`, customStyles: { cssClass: 'text-dpmedium' }
      })
    ];

  }

  loadCardsInfo(): void {

    this.cardsInfo = [
      new CardInfo({
        customStylesRow: { cssClass: 'col-3 ps-0 pe-1' },
        text: this.cloudService.isModeCloud() ? `${this.i18nPrefix}.cards.info.generated-transactions.label` : `${this.i18nPrefix}.cards.info.generated-documents.label`,
        textTooltip: this.cloudService.isModeCloud() ? `${this.i18nPrefix}.cards.info.generated-transactions.tooltip` : `${this.i18nPrefix}.cards.info.generated-documents.tooltip`,
        value: '',
        valueTooltip: '0'
      }),
      new CardInfo({
        customStylesRow: { cssClass: 'col-3 ps-1 pe-2' },
        text: `${this.i18nPrefix}.cards.info.generated-pages.label`,
        textTooltip: `${this.i18nPrefix}.cards.info.generated-pages.tooltip`,
        value: '',
        valueTooltip: '0'
      }),
      new CardInfo({
        customStylesRow: { cssClass: 'col-3 ps-2 pe-1' },
        text: `${this.i18nPrefix}.cards.info.executed-jobs.label`,
        textTooltip: `${this.i18nPrefix}.cards.info.executed-jobs.tooltip`,
        value: '',
        valueTooltip: '0'
      }),
      new CardInfo({
        customStylesRow: { cssClass: 'col-3 ps-1 pe-0' },
        text: `${this.i18nPrefix}.cards.info.errors.label`,
        textTooltip: `${this.i18nPrefix}.cards.info.errors.tooltip`,
        value: '',
        valueTooltip: '0',
        customStylesValue: { cssClass: 'text-dpred' }
      })
    ];

    this.loadCardsInfoValue();

  }

  private loadCardsInfoValue(): void {
    this.statisticsHttpService.getCardsInfo(
      new StatisticsCardSearch(this.dgeId, this.numberHours, this.customNumberHoursFrom, this.customNumberHoursTo, this.lastJobId)
    ).subscribe(result => {
      this.updateCardsInfoValues(result);
    });
  }

  private updateCardsInfoValues(result: StatisticsCardResult): void {
    const firstCardValue = this.cloudService.isModeCloud() ? result.generatedTransactions : result.generatedDocuments;

    this.updateCardInfo(0, firstCardValue);
    this.updateCardInfo(1, result.generatedPages);
    this.updateCardInfo(2, result.executedJobs);
    this.updateCardInfo(3, result.errors);
  }

  private updateCardInfo(index: number, value: number): void {
    const formattedValue = `${value}`;
    const card = this.cardsInfo[index];

    if (card) {
      card.value = formattedValue;
      card.valueTooltip = formattedValue;
      card.loading = false;
    }
  }

  loadDgesFiltersOptions(): Observable<void> {

    return new Observable(observer => {
      this.statisticsHttpService.getAllDge().subscribe(result => {
        this.dgesFilter.push(new SinclairCardFilterOption({
          type: SinclairCardFilterOptionType.DgeFilters,
          title: `${this.i18nPrefix}.cards.filters.dges.default.label`,
          subTitle: `${this.i18nPrefix}.cards.filters.dges.default.labelSuffix`,
          value: Constants.DGE_ALL_VALUES,
          tooltip: `${this.i18nPrefix}.cards.filters.dges.default.tooltip`,
          active: true
        }));

        result.forEach(dge => {
          this.dgesFilter.push(new SinclairCardFilterOption({
            type: SinclairCardFilterOptionType.DgeFilters,
            title: dge.name,
            subTitle: dge.dgeDescription,
            value: dge.id,
            tooltip: dge.name,
            labelState: dge.currentState,
            stateClass: { cssClass: this.getStateClassForDgeState(dge.currentState) }
          }));
        });

        observer.next();
        observer.complete();
      });
    });

  }

  private getStateClassForDgeState(dgeState: string): string {

    switch (dgeState) {
      case DgeState.RUNNING: return 'dpgreen';
      case DgeState.STOPPING: return 'dpyellow';
      default: return 'dpred';
    }

  }

  loadNumberOfHoursFiltersOptions() {

    this.numberOfHoursFilter = [
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.last-24-hours.label`,
        value: Constants.NUMBER_OF_HOURS_24, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.last-24-hours.tooltip`, active: true
      }),
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.last-12-hours.label`,
        value: Constants.NUMBER_OF_HOURS_12, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.last-12-hours.tooltip`
      }),
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.last-6-hours.label`,
        value: Constants.NUMBER_OF_HOURS_6, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.last-6-hours.tooltip`
      }),
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.last-hour.label`,
        value: Constants.NUMBER_OF_HOURS_1, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.last-hour.tooltip`
      }),
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.custom.label`,
        value: Constants.NUMBER_OF_HOURS_0, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.custom.tooltip`
      })
    ];

  }

  loadAllComponents(): void {

    const dge: number = this.sinclairCards.find(c => c.name === this.CARD_FILTERS_DGES)?.filters.find(f => f.active)?.value as number;
    const numberHours: number = this.sinclairCards.find(c => c.name === this.CARD_FILTERS_NUMBER_OF_HOURS)?.filters.find(f => f.active)?.value as number;

    if (numberHours === Constants.NUMBER_OF_HOURS_0) {
      const customDateRange = this.sinclairCards.find(c => c.name === this.CARD_FILTERS_NUMBER_OF_HOURS)?.title.split(' - ');

      this.customNumberHoursFrom = customDateRange![0];
      this.customNumberHoursTo = customDateRange![1];

      this.statisticsHttpService.getLastJobId(dge).subscribe(result => {
        this.loadAllComponentsValues(dge, numberHours, result);
      });
    } else {
      this.customNumberHoursFrom = '';
      this.customNumberHoursTo = '';

      this.loadAllComponentsValues(dge, numberHours, 0);
    }

  }

  private loadAllComponentsValues(dge: number, numberHours: number, lastJobId: number): void {
    this.dgeId = dge;
    this.numberHours = numberHours;
    this.lastJobId = lastJobId;
    this.statisticsService.notifyStatisticsSelectsChange(dge, numberHours, this.customNumberHoursFrom, this.customNumberHoursTo, lastJobId);

    this.loadCardsInfoValue();
  }

  cardFiltersClicked(card: SinclairCard): void {

    if (card.name === this.CARD_FILTERS_DGES) {
      const dgeIdActive = card.filters.find(f => f.active)?.value as number;
      this.dgesFilter = [];
      this.loadDgesFiltersOptions().subscribe(() => {
        card.filters = this.dgesFilter;

        // Set active false to all filters and active true to the filter with the dgeIdActive
        card.filters.find(f => f.value === Constants.DGE_ALL_VALUES)!.active = false;
        const filter = card.filters.find(f => f.value === dgeIdActive);
        if (filter) {
          filter.active = true;
        }
      });
    }

  }
  onCheckChange(): void {
    if (this.table) {
      this.selectedRow = this.statisticsTableService.getSelectedJob(this.table);
    }
  }

  getTable(): Table | undefined {
    return this.table;
  }

  onFilterChange(filters: Filter[]): void {
    this.filters = filters;
  }

  getSelectedJobCount(): number {
    if (this.table) {
      const jobs = this.statisticsTableService.getSelectedJob(this.table);

      return jobs.length;
    }

    return 0;
  }

  getSelectedJob(): Job | undefined {
    if (this.table) {
      const jobs = this.statisticsTableService.getSelectedJob(this.table);

      return jobs.length === 1 ? jobs[0] : undefined;
    }

    return undefined;
  }

  onColumnVisibilityChange(option: DpHeaderOption): void {
    const columnsConfig = this.tableService.getColumns(this.tableName),
      currentColumn = columnsConfig.find(column => column.name === option.value);

    if (currentColumn) {
      currentColumn.visible = option.checked;
      this.tableService.updateColumn(this.tableName, currentColumn);
    }

    if (this.table) {
      this.statisticsTableService.refreshColumns(this.table);
    }
  }

  private clearUrl(): void {

    const context = window.location.pathname.split('/')[1];
    const path = RoutingPath.PATH_STATISTICS;
    const url = `/${context}/${path}`;
    history.pushState({}, '', url);

  }

  private updateDgeFilterActiveByDgeIdUrl() {

    const card = this.sinclairCards.find(c => c.name === this.CARD_FILTERS_DGES);

    if (card) {
      card.filters.forEach(filter => {
        if (filter.value === this.dgeId) {
          filter.active = true;
        } else {
          filter.active = false;
        }
      });
    }

  }

}
